import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
    { v: "renovation_legere", l: "Rénovation légère", d: "Peinture, sols, rafraîchissement", price: 1400 },
    { v: "renovation_partielle", l: "Rénovation partielle", d: "Cuisine + SDB inclus", price: 2100 },
    { v: "renovation_complete", l: "Rénovation complète", d: "Tout compris, plomberie & électricité", price: 2800 },
    { v: "renovation_lourde", l: "Rénovation lourde", d: "Restructuration totale", price: 3800 },
    { v: "construction_standard", l: "Construction · standard", d: "Maison individuelle sur mesure", price: 2400 },
    { v: "construction_haut_gamme", l: "Construction · haut de gamme", d: "Matériaux nobles, prestations soignées", price: 3500 },
    { v: "construction_luxe", l: "Construction · luxe", d: "Villa d'architecte, prestations premium", price: 5200 },
];

const FINISH = [
    { v: "essentiel", l: "Essentiel", d: "Belles finitions standard" },
    { v: "confort", l: "Confort", d: "Matériaux qualitatifs, sur mesure partiel" },
    { v: "prestige", l: "Prestige", d: "Sur mesure intégral, marbres, essences rares" },
];

const OPTIONS = [
    { v: "cuisine_equipee", l: "Cuisine équipée haut de gamme" },
    { v: "salle_bain_premium", l: "Salle de bain premium" },
    { v: "domotique", l: "Domotique intégrée" },
    { v: "climatisation", l: "Climatisation réversible" },
    { v: "parquet_massif", l: "Parquet massif chêne" },
    { v: "sur_mesure_menuiserie", l: "Menuiseries sur mesure" },
    { v: "cheminee", l: "Restauration cheminée" },
    { v: "isolation_renforcee", l: "Isolation renforcée" },
];

const fmt = (n) =>
    new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n);

export default function QuoteSimulator() {
    const [step, setStep] = useState(1);
    const [type, setType] = useState("renovation_complete");
    const [surface, setSurface] = useState(80);
    const [finish, setFinish] = useState("confort");
    const [stories, setStories] = useState(1);
    const [options, setOptions] = useState([]);
    const [breakdown, setBreakdown] = useState(null);
    const [busy, setBusy] = useState(false);

    const [contactOpen, setContactOpen] = useState(false);
    const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "" });
    const [laborRates, setLaborRates] = useState(null);
    const [showRates, setShowRates] = useState(false);
    const [estEmail, setEstEmail] = useState("");
    const [estEmailBusy, setEstEmailBusy] = useState(false);

    useEffect(() => {
        axios.get(`${API}/labor-rates`).then((r) => setLaborRates(r.data)).catch(() => {});
    }, []);

    const sendEstimateEmail = async (e) => {
        e.preventDefault();
        if (!estEmail) return;
        setEstEmailBusy(true);
        try {
            await axios.post(`${API}/quote/email-estimate`, { email: estEmail, input: payload });
            toast.success("Estimation envoyée par email ✓ — pensez à vérifier vos spams");
            setEstEmail("");
        } catch {
            toast.error("Erreur d'envoi. Vérifiez votre email.");
        } finally {
            setEstEmailBusy(false);
        }
    };

    const isConstruction = type.startsWith("construction");

    const payload = useMemo(
        () => ({
            project_type: type,
            surface_m2: Number(surface) || 0,
            finish_level: finish,
            stories: isConstruction ? Number(stories) : 1,
            options,
        }),
        [type, surface, finish, stories, options, isConstruction],
    );

    useEffect(() => {
        if (payload.surface_m2 <= 0) {
            setBreakdown(null);
            return;
        }
        const t = setTimeout(async () => {
            try {
                const r = await axios.post(`${API}/quote/calculate`, payload);
                setBreakdown(r.data);
            } catch (e) {
                console.error(e);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [payload]);

    const toggleOption = (v) =>
        setOptions((prev) =>
            prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
        );

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email) {
            toast.error("Merci de renseigner votre nom et email.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/quote/request`, {
                ...form,
                input: payload,
            });
            toast.success("Demande envoyée. Nous vous contactons sous 48 h.");
            setContactOpen(false);
            setForm({ full_name: "", email: "", phone: "", message: "" });
        } catch (e) {
            toast.error("Une erreur est survenue. Réessayez.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            id="devis"
            data-testid="quote-simulator"
            className="py-32 bg-[#FAF9F6] relative"
        >
            <div className="max-w-[1500px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
                    {/* Left intro */}
                    <div className="lg:col-span-4 lg:sticky lg:top-28 section-fade">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-[#B89C6A]" />
                            <span className="tracking-luxe text-[#B89C6A]">
                                Estimateur
                            </span>
                        </div>
                        <h2 className="font-serif-lux text-4xl md:text-5xl leading-[1.02]">
                            Un devis <em className="italic text-[#B89C6A]">réaliste</em>, en trois étapes.
                        </h2>
                        <p className="mt-6 text-[14px] leading-relaxed text-[#1C1E1D]/70">
                            Notre estimateur s'appuie sur les prix constatés en 2025 en Île-de-France, du <b>bas de gamme au haut de gamme</b>. La fourchette affichée reflète l'ensemble du marché : matériaux, finitions, options. Un devis détaillé est établi après visite gratuite sur site.
                        </p>

                        <div className="mt-10 space-y-1">
                            {["Nature du projet", "Surface & finitions", "Options premium"].map(
                                (t, i) => (
                                    <div
                                        key={t}
                                        className={`flex items-center gap-4 py-3 border-b hairline transition-opacity ${
                                            step === i + 1
                                                ? "opacity-100"
                                                : "opacity-40"
                                        }`}
                                    >
                                        <span className="font-serif-lux text-3xl text-[#B89C6A]">
                                            0{i + 1}
                                        </span>
                                        <span className="tracking-luxe">
                                            {t}
                                        </span>
                                    </div>
                                ),
                            )}
                        </div>
                    </div>

                    {/* Right simulator */}
                    <div className="lg:col-span-8 section-fade">
                        <div className="lux-card p-8 md:p-12">
                            {/* Step 1 */}
                            {step === 1 && (
                                <div data-testid="quote-step-1">
                                    <h3 className="tracking-luxe text-[#B89C6A] mb-6">
                                        01 · Quelle est la nature de votre projet ?
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {PROJECT_TYPES.map((p) => (
                                            <button
                                                key={p.v}
                                                data-testid={`type-${p.v}`}
                                                onClick={() => setType(p.v)}
                                                className={`text-left p-5 border transition-all ${
                                                    type === p.v
                                                        ? "border-[#B89C6A] bg-[#B89C6A]/8"
                                                        : "border-[#1C1E1D]/15 hover:border-[#1C1E1D]/40"
                                                }`}
                                            >
                                                <div className="font-serif-lux text-xl">
                                                    {p.l}
                                                </div>
                                                <div className="text-[13px] text-[#1C1E1D]/60 mt-1">
                                                    {p.d}
                                                </div>
                                                <div className="tracking-luxe text-[#B89C6A] mt-3 text-[10px]">
                                                    À partir de {fmt(p.price)} €/m²
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Step 2 */}
                            {step === 2 && (
                                <div data-testid="quote-step-2" className="space-y-10">
                                    <div>
                                        <h3 className="tracking-luxe text-[#B89C6A] mb-6">
                                            02 · Surface &amp; finitions
                                        </h3>
                                        <label className="block mb-8">
                                            <div className="flex justify-between mb-3">
                                                <span className="text-[13px] tracking-wider uppercase">
                                                    Surface
                                                </span>
                                                <span className="font-serif-lux text-2xl">
                                                    {surface} m²
                                                </span>
                                            </div>
                                            <input
                                                data-testid="surface-slider"
                                                type="range"
                                                min={20}
                                                max={500}
                                                step={5}
                                                value={surface}
                                                onChange={(e) =>
                                                    setSurface(e.target.value)
                                                }
                                                className="w-full accent-[#B89C6A]"
                                            />
                                            <div className="flex justify-between text-[11px] text-[#1C1E1D]/50 mt-2">
                                                <span>20 m²</span>
                                                <span>500 m²</span>
                                            </div>
                                        </label>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            {FINISH.map((f) => (
                                                <button
                                                    key={f.v}
                                                    data-testid={`finish-${f.v}`}
                                                    onClick={() =>
                                                        setFinish(f.v)
                                                    }
                                                    className={`text-left p-4 border transition-all ${
                                                        finish === f.v
                                                            ? "border-[#B89C6A] bg-[#B89C6A]/8"
                                                            : "border-[#1C1E1D]/15 hover:border-[#1C1E1D]/40"
                                                    }`}
                                                >
                                                    <div className="font-serif-lux text-lg">
                                                        {f.l}
                                                    </div>
                                                    <div className="text-[12px] text-[#1C1E1D]/60 mt-1">
                                                        {f.d}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {isConstruction && (
                                        <div>
                                            <div className="tracking-luxe mb-4">
                                                Nombre de niveaux
                                            </div>
                                            <div className="flex gap-2">
                                                {[1, 2, 3, 4].map((n) => (
                                                    <button
                                                        key={n}
                                                        data-testid={`stories-${n}`}
                                                        onClick={() =>
                                                            setStories(n)
                                                        }
                                                        className={`w-14 h-14 border font-serif-lux text-xl transition-all ${
                                                            stories === n
                                                                ? "border-[#B89C6A] bg-[#B89C6A]/10"
                                                                : "border-[#1C1E1D]/20 hover:border-[#1C1E1D]/50"
                                                        }`}
                                                    >
                                                        {n}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Step 3 */}
                            {step === 3 && (
                                <div data-testid="quote-step-3">
                                    <h3 className="tracking-luxe text-[#B89C6A] mb-6">
                                        03 · Options premium
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {OPTIONS.map((o) => {
                                            const active = options.includes(
                                                o.v,
                                            );
                                            return (
                                                <button
                                                    key={o.v}
                                                    data-testid={`option-${o.v}`}
                                                    onClick={() =>
                                                        toggleOption(o.v)
                                                    }
                                                    className={`flex items-center gap-4 p-4 border text-left transition-all ${
                                                        active
                                                            ? "border-[#B89C6A] bg-[#B89C6A]/8"
                                                            : "border-[#1C1E1D]/15 hover:border-[#1C1E1D]/40"
                                                    }`}
                                                >
                                                    <span
                                                        className={`w-5 h-5 border flex items-center justify-center ${
                                                            active
                                                                ? "bg-[#B89C6A] border-[#B89C6A]"
                                                                : "border-[#1C1E1D]/40"
                                                        }`}
                                                    >
                                                        {active && (
                                                            <span className="text-[#FAF9F6] text-xs">
                                                                ✓
                                                            </span>
                                                        )}
                                                    </span>
                                                    <span className="font-serif-lux text-lg">
                                                        {o.l}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Nav */}
                            <div className="flex justify-between items-center mt-10 pt-8 border-t hairline">
                                <button
                                    data-testid="step-prev"
                                    onClick={() =>
                                        setStep((s) => Math.max(1, s - 1))
                                    }
                                    disabled={step === 1}
                                    className="btn-outline disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    ← Retour
                                </button>
                                {step < 3 ? (
                                    <button
                                        data-testid="step-next"
                                        onClick={() =>
                                            setStep((s) => Math.min(3, s + 1))
                                        }
                                        className="btn-champagne"
                                    >
                                        Continuer →
                                    </button>
                                ) : (
                                    <button
                                        data-testid="open-quote-contact"
                                        onClick={() => setContactOpen(true)}
                                        className="btn-champagne"
                                    >
                                        Recevoir mon devis
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Live estimate card */}
                        {breakdown && (
                            <div
                                data-testid="quote-estimate"
                                className="mt-6 p-8 bg-[#1C1E1D] text-[#FAF9F6]"
                            >
                                <div className="flex items-baseline justify-between flex-wrap gap-4">
                                    <div>
                                        <div className="tracking-luxe text-[#B89C6A]">
                                            Fourchette marché · du bas au haut de gamme
                                        </div>
                                        <div className="font-serif-lux text-4xl md:text-5xl mt-3">
                                            {fmt(breakdown.total_min)} €
                                            <span className="text-[#B89C6A] mx-3">
                                                —
                                            </span>
                                            {fmt(breakdown.total_max)} €
                                        </div>
                                        <div className="text-[12px] text-[#FAF9F6]/60 mt-2">
                                            Soit environ{" "}
                                            {fmt(breakdown.subtotal_per_m2)}{" "}
                                            €/m² · aléas de chantier inclus
                                            (10 %). Prix indicatifs HT observés en Île-de-France.
                                        </div>
                                    </div>
                                    <div className="text-right text-[12px] text-[#FAF9F6]/60 space-y-1">
                                        <div>
                                            Base : {fmt(breakdown.base_price_per_m2)} €/m²
                                        </div>
                                        <div>
                                            Finition ×{breakdown.finish_multiplier}
                                        </div>
                                        {breakdown.story_multiplier > 1 && (
                                            <div>
                                                Niveaux ×{breakdown.story_multiplier}
                                            </div>
                                        )}
                                        {breakdown.options_per_m2 > 0 && (
                                            <div>
                                                Options : +{fmt(breakdown.options_per_m2)} €/m²
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-6 pt-4 border-t border-[#FAF9F6]/10 flex items-center gap-3">
                                    <span className="w-2 h-2 rounded-full bg-[#B89C6A]" />
                                    <span className="text-[11px] tracking-widest uppercase text-[#FAF9F6]/60">
                                        Bas de gamme
                                    </span>
                                    <div className="flex-1 h-px bg-gradient-to-r from-[#FAF9F6]/20 via-[#B89C6A] to-[#FAF9F6]/20" />
                                    <span className="text-[11px] tracking-widest uppercase text-[#FAF9F6]/60">
                                        Haut de gamme
                                    </span>
                                    <span className="w-2 h-2 rounded-full bg-[#B89C6A]" />
                                </div>

                                {breakdown.labor_min > 0 && (
                                    <div className="mt-5 pt-4 border-t border-[#FAF9F6]/10">
                                        <div className="flex justify-between items-baseline flex-wrap gap-2">
                                            <span className="text-[12px] text-[#FAF9F6]/70">
                                                Dont main d'œuvre estimée <span className="text-[#B89C6A]">(≈{breakdown.labor_share_pct} % du budget)</span>
                                            </span>
                                            <span data-testid="labor-estimate" className="font-serif-lux text-xl text-[#B89C6A]">
                                                {fmt(breakdown.labor_min)} — {fmt(breakdown.labor_max)} €
                                            </span>
                                        </div>
                                        <button
                                            data-testid="labor-rates-toggle"
                                            onClick={() => setShowRates((s) => !s)}
                                            className="mt-2 text-[10px] tracking-wider uppercase underline text-[#FAF9F6]/50 hover:text-[#B89C6A] transition-colors"
                                        >
                                            Tarifs horaires moyens du marché (IDF {laborRates?.year || 2026}) {showRates ? "▲" : "▼"}
                                        </button>
                                        {showRates && laborRates && (
                                            <div data-testid="labor-rates-table" className="mt-3">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0.5">
                                                    {laborRates.rates.map((r) => (
                                                        <div key={r.trade} className="flex justify-between text-[11px] text-[#FAF9F6]/70 border-b border-[#FAF9F6]/10 py-1.5">
                                                            <span>{r.trade}</span>
                                                            <span className="text-[#B89C6A] whitespace-nowrap">{r.min} – {r.max} €/h</span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <p className="text-[10px] italic text-[#FAF9F6]/40 mt-3 leading-relaxed">{laborRates.note}</p>
                                            </div>
                                        )}
                                        <form onSubmit={sendEstimateEmail} className="mt-4 flex flex-col sm:flex-row gap-2">
                                            <input
                                                data-testid="estimate-email-input"
                                                type="email" required
                                                placeholder="Recevoir cette estimation détaillée par email"
                                                className="flex-1 bg-transparent border border-[#FAF9F6]/25 px-4 py-2.5 text-[13px] placeholder:text-[#FAF9F6]/40 focus:outline-none focus:border-[#B89C6A]"
                                                value={estEmail}
                                                onChange={(e) => setEstEmail(e.target.value)}
                                            />
                                            <button
                                                data-testid="estimate-email-send"
                                                type="submit" disabled={estEmailBusy}
                                                className="inline-flex justify-center items-center px-5 py-2.5 bg-[#B89C6A] text-[#1C1E1D] text-[10px] font-medium tracking-[0.18em] uppercase hover:bg-[#FAF9F6] transition-colors disabled:opacity-50"
                                            >
                                                {estEmailBusy ? "Envoi…" : "Recevoir par email"}
                                            </button>
                                        </form>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Contact modal for quote */}
            {contactOpen && (
                <div
                    data-testid="quote-contact-modal"
                    className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setContactOpen(false)}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={submit}
                        className="bg-[#FAF9F6] w-full max-w-lg p-10"
                    >
                        <div className="tracking-luxe text-[#B89C6A]">
                            Recevoir mon devis détaillé
                        </div>
                        <h3 className="font-serif-lux text-3xl mt-3 mb-8">
                            Vos coordonnées
                        </h3>
                        <div className="space-y-6">
                            <input
                                data-testid="quote-form-name"
                                className="lux-input"
                                placeholder="Nom et prénom"
                                value={form.full_name}
                                onChange={(e) =>
                                    setForm({ ...form, full_name: e.target.value })
                                }
                                required
                            />
                            <input
                                data-testid="quote-form-email"
                                className="lux-input"
                                type="email"
                                placeholder="Email"
                                value={form.email}
                                onChange={(e) =>
                                    setForm({ ...form, email: e.target.value })
                                }
                                required
                            />
                            <input
                                data-testid="quote-form-phone"
                                className="lux-input"
                                placeholder="Téléphone"
                                value={form.phone}
                                onChange={(e) =>
                                    setForm({ ...form, phone: e.target.value })
                                }
                            />
                            <textarea
                                data-testid="quote-form-message"
                                className="lux-input min-h-[80px] resize-none"
                                placeholder="Message (facultatif)"
                                value={form.message}
                                onChange={(e) =>
                                    setForm({ ...form, message: e.target.value })
                                }
                            />
                        </div>
                        <div className="flex justify-between mt-10">
                            <button
                                type="button"
                                data-testid="quote-form-cancel"
                                onClick={() => setContactOpen(false)}
                                className="btn-outline"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                data-testid="quote-form-submit"
                                disabled={busy}
                                className="btn-champagne disabled:opacity-50"
                            >
                                {busy ? "Envoi…" : "Envoyer"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </section>
    );
}
